import { useState } from 'react'
import { cn } from '@/lib/utils'
import {
    CheckCircle2,
    Loader2,
    XCircle,
    ChevronDown,
    ChevronRight,
    Hash,
    FileText,
    GitBranch,
    Search,
    Plus,
    Pencil,
    Trash2,
    BarChart3,
    Eye,
    StickyNote,
    Settings2,
    AlertTriangle,
} from 'lucide-react'

type Props = {
    name: string
    status: 'running' | 'done'
    success?: boolean
    durationMs?: number
    args?: Record<string, unknown>
    result?: unknown
}

const FRIENDLY_TOOL_NAMES: Record<string, string> = {
    query_keywords: 'Searching keywords',
    get_keyword_by_id: 'Fetching keyword',
    get_keyword_schema: 'Reading keyword schema',
    create_keyword: 'Creating keyword',
    update_keyword: 'Updating keyword',
    add_note_to_keyword: 'Adding note',
    aggregate_keywords: 'Aggregating keywords',
    count_keywords_by_status: 'Counting keywords',
    list_documents: 'Searching documents',
    get_document_by_id: 'Fetching document',
    get_document_schema: 'Reading document schema',
    create_document: 'Creating document',
    update_document: 'Updating document',
    write_document_content: 'Writing document',
    delete_document: 'Deleting document',
    aggregate_documents: 'Aggregating documents',
    count_documents: 'Counting documents',
    get_pipeline_config: 'Reading pipeline config',
    add_pipeline_group: 'Adding pipeline group',
    update_pipeline_group: 'Updating pipeline group',
    add_pipeline_subgroup: 'Adding subgroup',
    update_pipeline_subgroup: 'Updating subgroup',
}

const FRIENDLY_DONE_NAMES: Record<string, string> = {
    query_keywords: 'Searched keywords',
    get_keyword_by_id: 'Fetched keyword',
    get_keyword_schema: 'Read keyword schema',
    create_keyword: 'Created keyword',
    update_keyword: 'Updated keyword',
    add_note_to_keyword: 'Added note',
    aggregate_keywords: 'Aggregated keywords',
    count_keywords_by_status: 'Counted keywords',
    list_documents: 'Searched documents',
    get_document_by_id: 'Fetched document',
    get_document_schema: 'Read document schema',
    create_document: 'Created document',
    update_document: 'Updated document',
    write_document_content: 'Wrote document',
    delete_document: 'Deleted document',
    aggregate_documents: 'Aggregated documents',
    count_documents: 'Counted documents',
    get_pipeline_config: 'Read pipeline config',
    add_pipeline_group: 'Added pipeline group',
    update_pipeline_group: 'Updated pipeline group',
    add_pipeline_subgroup: 'Added subgroup',
    update_pipeline_subgroup: 'Updated subgroup',
}

const getToolIcon = (name: string) => {
    if (name.includes('keyword')) {
        if (name.startsWith('query') || name.startsWith('list')) return Search
        if (name.startsWith('create')) return Plus
        if (name.startsWith('update')) return Pencil
        if (name.startsWith('delete')) return Trash2
        if (name.startsWith('aggregate') || name.startsWith('count')) return BarChart3
        if (name.includes('note')) return StickyNote
        if (name.includes('schema')) return Eye
        return Hash
    }
    if (name.includes('document')) {
        if (name.startsWith('list') || name.startsWith('query')) return Search
        if (name.startsWith('create')) return Plus
        if (name.startsWith('update') || name.startsWith('write')) return Pencil
        if (name.startsWith('delete')) return Trash2
        if (name.startsWith('aggregate') || name.startsWith('count')) return BarChart3
        if (name.includes('schema')) return Eye
        return FileText
    }
    if (name.includes('pipeline')) {
        if (name.startsWith('add')) return Plus
        if (name.startsWith('update')) return Pencil
        return GitBranch
    }
    return Settings2
}

const getToolFamily = (name: string): 'keywords' | 'documents' | 'pipeline' | 'other' => {
    if (name.includes('keyword')) return 'keywords'
    if (name.includes('document')) return 'documents'
    if (name.includes('pipeline')) return 'pipeline'
    return 'other'
}

const FAMILY_COLORS: Record<string, { bg: string; text: string; ring: string; iconBg: string }> = {
    keywords: {
        bg: 'bg-blue-50',
        text: 'text-blue-700',
        ring: 'ring-blue-100',
        iconBg: 'bg-blue-100 text-blue-600',
    },
    documents: {
        bg: 'bg-purple-50',
        text: 'text-purple-700',
        ring: 'ring-purple-100',
        iconBg: 'bg-purple-100 text-purple-600',
    },
    pipeline: {
        bg: 'bg-amber-50',
        text: 'text-amber-700',
        ring: 'ring-amber-100',
        iconBg: 'bg-amber-100 text-amber-600',
    },
    other: {
        bg: 'bg-slate-50',
        text: 'text-slate-700',
        ring: 'ring-slate-100',
        iconBg: 'bg-slate-100 text-slate-600',
    },
}

const getFriendlyName = (name: string, status: 'running' | 'done'): string => {
    if (status === 'running') {
        return FRIENDLY_TOOL_NAMES[name] || name.replace(/_/g, ' ')
    }
    return FRIENDLY_DONE_NAMES[name] || name.replace(/_/g, ' ')
}

const formatValue = (value: unknown): string => {
    if (value === null) return 'null'
    if (value === undefined) return 'undefined'
    if (typeof value === 'string') return value
    if (typeof value === 'number' || typeof value === 'boolean') return String(value)
    if (Array.isArray(value)) return JSON.stringify(value)
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
}

const PARAM_PRIORITY = [
    'id',
    'keywordId',
    'documentId',
    'correlationId',
    'correlationIds',
    'search',
    'name',
    'text',
    'status',
    'group',
    'subGroup',
    'groupLabel',
    'subGroupLabel',
    'tag',
    'isFolder',
    'parentId',
    'hasOwner',
    'owner',
    'approver',
    'sortBy',
    'sortOrder',
    'limit',
    'maxResults',
    'pipeline',
    'confirm',
    'confirmationText',
]

const sortArgEntries = (args: Record<string, unknown>): [string, unknown][] => {
    const entries = Object.entries(args)
    const priorityIndex = (key: string) => {
        const i = PARAM_PRIORITY.indexOf(key)
        return i === -1 ? PARAM_PRIORITY.length : i
    }
    return entries.sort((a, b) => {
        const ai = priorityIndex(a[0])
        const bi = priorityIndex(b[0])
        if (ai !== bi) return ai - bi
        return a[0].localeCompare(b[0])
    })
}

const isValidationError = (result: unknown): boolean => {
    return (
        typeof result === 'object' &&
        result !== null &&
        (result as any).validationError === true
    )
}

export default function ToolCallCard({ name, status, success, durationMs, args, result }: Props) {
    const [expanded, setExpanded] = useState(false)
    const Icon = getToolIcon(name)
    const family = getToolFamily(name)
    const colors = FAMILY_COLORS[family]
    const hasArgs = args && Object.keys(args).length > 0
    const hasResult = result !== undefined && status === 'done'
    const canExpand = hasArgs || hasResult
    const validationFailed = isValidationError(result)
    const isFailed = status === 'done' && (!success || validationFailed)

    return (
        <div
            className={cn(
                'group rounded-xl bg-white ring-1 transition-all',
                isFailed
                    ? 'ring-red-200'
                    : status === 'running'
                      ? cn(colors.ring, 'shadow-sm')
                      : 'ring-slate-200'
            )}
        >
            <button
                type="button"
                onClick={() => canExpand && setExpanded(!expanded)}
                className={cn(
                    'flex w-full items-center gap-3 px-3 py-2.5 text-left',
                    canExpand && 'cursor-pointer hover:bg-slate-50/50'
                )}
            >
                <div
                    className={cn(
                        'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg',
                        isFailed ? 'bg-red-50 text-red-500' : colors.iconBg
                    )}
                >
                    {status === 'running' ? (
                        <Loader2 className='h-3.5 w-3.5 animate-spin' />
                    ) : (
                        <Icon className='h-3.5 w-3.5' />
                    )}
                </div>

                <div className='min-w-0 flex-1'>
                    <div className='flex items-center gap-2'>
                        <span
                            className={cn(
                                'text-sm font-medium',
                                isFailed ? 'text-red-700' : 'text-slate-900'
                            )}
                        >
                            {getFriendlyName(name, status)}
                        </span>
                        {status === 'done' && (
                            isFailed ? (
                                validationFailed ? (
                                    <AlertTriangle className='h-3.5 w-3.5 text-amber-500' />
                                ) : (
                                    <XCircle className='h-3.5 w-3.5 text-red-500' />
                                )
                            ) : (
                                <CheckCircle2 className='h-3.5 w-3.5 text-green-500' />
                            )
                        )}
                    </div>
                    {hasArgs && !expanded && (
                        <div className='mt-0.5 truncate text-xs text-slate-500'>
                            {sortArgEntries(args).slice(0, 3).map(([k, v], i) => (
                                <span key={k}>
                                    {i > 0 && <span className='mx-1.5 text-slate-300'>·</span>}
                                    <span className='text-slate-400'>{k}=</span>
                                    <span className='font-mono'>{formatValue(v).slice(0, 40)}</span>
                                </span>
                            ))}
                            {Object.keys(args).length > 3 && (
                                <span className='text-slate-400'> +{Object.keys(args).length - 3} more</span>
                            )}
                        </div>
                    )}
                </div>

                <div className='flex shrink-0 items-center gap-2'>
                    {status === 'done' && durationMs !== undefined && (
                        <span className='text-[10px] tabular-nums text-slate-400'>
                            {durationMs < 1000 ? `${Math.round(durationMs)}ms` : `${(durationMs / 1000).toFixed(1)}s`}
                        </span>
                    )}
                    {canExpand && (
                        expanded ? (
                            <ChevronDown className='h-3.5 w-3.5 text-slate-400' />
                        ) : (
                            <ChevronRight className='h-3.5 w-3.5 text-slate-400' />
                        )
                    )}
                </div>
            </button>

            {expanded && canExpand && (
                <div className='border-t border-slate-100 px-3 py-3 space-y-3'>
                    {hasArgs && (
                        <div>
                            <div className='mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400'>
                                Parameters
                            </div>
                            <div className='space-y-1 rounded-lg bg-slate-50 p-2.5'>
                                {sortArgEntries(args).map(([key, value]) => (
                                    <div key={key} className='flex gap-2 text-xs'>
                                        <span className='shrink-0 font-mono text-slate-500'>{key}</span>
                                        <span className='text-slate-300'>=</span>
                                        <span className='break-all font-mono text-slate-700'>
                                            {formatValue(value)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {hasResult && (
                        <div>
                            <div className='mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400'>
                                {validationFailed ? 'Validation Error' : 'Result'}
                            </div>
                            <pre
                                className={cn(
                                    'max-h-48 overflow-auto rounded-lg p-2.5 text-xs font-mono',
                                    validationFailed
                                        ? 'bg-amber-50 text-amber-900 ring-1 ring-amber-100'
                                        : 'bg-slate-900 text-slate-100'
                                )}
                            >
                                {typeof result === 'object'
                                    ? JSON.stringify(result, null, 2)
                                    : String(result)}
                            </pre>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
